import React from 'react';
import AddLinkIcon from '@mui/icons-material/AddLink';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import ClearIcon from '@mui/icons-material/Clear';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import InsertLinkIcon from '@mui/icons-material/InsertLink';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import VisibilityIcon from '@mui/icons-material/Visibility';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Divider from '@mui/material/Divider';
import IconButton from '@mui/material/IconButton';
import MenuItem from '@mui/material/MenuItem';
import Paper from '@mui/material/Paper';
import Select from '@mui/material/Select';
import Skeleton from '@mui/material/Skeleton';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';

import {
  formatDate,
  formatDateTime,
  formatDateTimeCompact,
  formatMoney,
} from '../utils/paymentLinks.utils';
import { DetailField } from './DetailField.component';
import { StatusChip } from './StatusChip.component';
import { TypeBadge } from './TypeIndicators.component';

interface LinksListProps {
  filters?: React.ReactNode;
  links: any[];
  loadedLinkCount: number;
  pagedLinks: any[];
  loading: boolean;
  isCompact: boolean;
  page: number;
  pageCount: number;
  rowsPerPage: number;
  setPage: (updater: (current: number) => number) => void;
  setRowsPerPage: (value: number) => void;
  onCopy: (link: string) => void;
  onDetail: (item: any) => void;
  onMenuOpen: (event: React.MouseEvent<HTMLElement>, item: any) => void;
  hasActiveFilters: boolean;
  onClearFilters: () => void;
  onCreate: () => void;
  onLoadMore: () => void;
  loadingMore: boolean;
  totalAvailableLinks: number;
}

export const LinksList = ({
  filters,
  links,
  loadedLinkCount,
  pagedLinks,
  loading,
  isCompact,
  page,
  pageCount,
  rowsPerPage,
  setPage,
  setRowsPerPage,
  onCopy,
  onDetail,
  onMenuOpen,
  hasActiveFilters,
  onClearFilters,
  onCreate,
  onLoadMore,
  loadingMore,
  totalAvailableLinks,
}: LinksListProps) => {
  const hasMoreLinks = loadedLinkCount < totalAvailableLinks;
  // Ações por linha: copiar link + ver detalhes + menu com o restante.
  const renderRowActions = (item: any) => (
    <Box
      display="flex"
      alignItems="center"
      justifyContent="flex-end"
      sx={{ '& .MuiIconButton-root': { p: 0.5 } }}
    >
      <Tooltip title="Ver detalhes">
        <IconButton size="small" onClick={() => onDetail(item)}>
          <VisibilityIcon fontSize="small" />
        </IconButton>
      </Tooltip>
      <Tooltip title="Copiar link">
        <span>
          <IconButton
            size="small"
            disabled={!item.link_url}
            onClick={() => onCopy(item.link_url)}
          >
            <ContentCopyIcon fontSize="small" />
          </IconButton>
        </span>
      </Tooltip>
      <Tooltip title="Ações">
        <IconButton size="small" onClick={(event) => onMenuOpen(event, item)}>
          <MoreVertIcon fontSize="small" />
        </IconButton>
      </Tooltip>
    </Box>
  );

  const emptyState = (
    <Box
      py={6}
      display="flex"
      flexDirection="column"
      alignItems="center"
      gap={1.5}
    >
      <InsertLinkIcon sx={{ fontSize: 48, color: 'text.disabled' }} />
      <Typography color="text.secondary">
        {hasActiveFilters
          ? 'Nenhum link encontrado para os filtros aplicados.'
          : 'Nenhum link criado ainda.'}
      </Typography>
      {hasActiveFilters ? (
        <Button size="small" startIcon={<ClearIcon />} onClick={onClearFilters}>
          Limpar filtros
        </Button>
      ) : (
        <Button
          size="small"
          variant="contained"
          startIcon={<AddLinkIcon />}
          onClick={onCreate}
        >
          Criar primeiro link
        </Button>
      )}
    </Box>
  );

  return (
    <Paper
      sx={{
        borderRadius: 2,
        border: '1px solid',
        borderColor: 'divider',
        boxShadow: '0 8px 24px rgba(17, 24, 39, 0.05)',
        overflow: 'hidden',
      }}
    >
      {filters && (
        <Box px={{ xs: 1, md: 1.25 }} py={1.25}>
          {filters}
        </Box>
      )}

      {/* Telas menores (inclui PDV como caixa): cartões. lg+: tabela densa. */}
      {isCompact ? (
        <Box
          display="grid"
          gridTemplateColumns={{
            xs: '1fr',
            sm: 'repeat(auto-fill, minmax(300px, 1fr))',
          }}
          gap={1.25}
          p={1.5}
        >
          {loading &&
            !links.length &&
            Array.from({ length: 4 }).map((_, index) => (
              <Skeleton
                key={`skeleton-card-${index}`}
                variant="rectangular"
                height={132}
                sx={{ borderRadius: 2 }}
              />
            ))}

          {pagedLinks.map((item) => (
            <Paper
              key={item.id}
              variant="outlined"
              sx={{ borderRadius: 2, p: 2 }}
            >
              <Box
                display="flex"
                justifyContent="space-between"
                alignItems="flex-start"
                gap={1}
              >
                <Box minWidth={0}>
                  <Typography variant="subtitle2" fontWeight={700} noWrap>
                    {item.description || '-'}
                  </Typography>
                  <Typography variant="caption" color="text.secondary" noWrap>
                    {item.destination || item.reference_id || '—'}
                  </Typography>
                </Box>
                <StatusChip status={item.status} />
              </Box>

              <Box
                display="flex"
                alignItems="center"
                justifyContent="space-between"
                mt={1.5}
              >
                <TypeBadge type={item.type} />
                <Typography variant="h6" fontWeight={700}>
                  {formatMoney(item.amount)}
                </Typography>
              </Box>

              <Box
                display="grid"
                gridTemplateColumns="1fr 1fr"
                gap={1}
                mt={1.5}
              >
                <DetailField label="Referência">
                  {item.external_reference}
                </DetailField>
                <DetailField label="Vencimento">
                  {formatDate(item.due_date)}
                </DetailField>
                <DetailField label="Criado em">
                  {formatDateTime(item.created_at)}
                </DetailField>
              </Box>

              <Divider sx={{ my: 1.5 }} />
              {renderRowActions(item)}
            </Paper>
          ))}

          {!loading && !links.length && (
            <Box gridColumn="1 / -1">{emptyState}</Box>
          )}
        </Box>
      ) : (
        <TableContainer
          sx={{
            maxHeight: {
              md: 'max(280px, calc(100vh - 278px))',
              xl: 'max(320px, calc(100vh - 260px))',
            },
          }}
        >
          <Table
            stickyHeader
            size="small"
            sx={{
              minWidth: 1120,
              '& .MuiTableCell-root': {
                fontSize: 12,
                lineHeight: 1.25,
                px: 0.75,
                py: 0.45,
              },
              '& .MuiTableHead .MuiTableCell-root': {
                bgcolor: 'grey.50',
                color: 'text.secondary',
                fontSize: 10.5,
                fontWeight: 800,
                py: 0.55,
              },
              '& .MuiTableBody .MuiTableRow-root': {
                height: 38,
              },
            }}
          >
            <TableHead>
              <TableRow>
                <TableCell sx={{ width: 98 }}>Status</TableCell>
                <TableCell sx={{ width: 92 }}>Tipo</TableCell>
                <TableCell sx={{ minWidth: 220 }}>Descrição</TableCell>
                <TableCell sx={{ width: 128 }}>Destino</TableCell>
                <TableCell sx={{ width: 92 }} align="right">
                  Valor
                </TableCell>
                <TableCell sx={{ width: 145 }}>Referência</TableCell>
                <TableCell sx={{ width: 96 }}>Vencimento</TableCell>
                <TableCell sx={{ width: 108 }}>Criado em</TableCell>
                <TableCell sx={{ width: 96 }} align="right">
                  Ações
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading &&
                !links.length &&
                Array.from({ length: 6 }).map((_, index) => (
                  <TableRow key={`skeleton-${index}`}>
                    {Array.from({ length: 9 }).map((__, cell) => (
                      <TableCell key={cell}>
                        <Skeleton variant="text" />
                      </TableCell>
                    ))}
                  </TableRow>
                ))}

              {pagedLinks.map((item) => (
                <TableRow key={item.id} hover>
                  <TableCell>
                    <StatusChip compact status={item.status} />
                  </TableCell>
                  <TableCell>
                    <TypeBadge compact type={item.type} />
                  </TableCell>
                  <TableCell>
                    <Box minWidth={0}>
                      <Tooltip title={item.description || '-'}>
                        <Typography
                          variant="body2"
                          fontWeight={700}
                          noWrap
                          sx={{
                            fontSize: 12.5,
                            lineHeight: 1.2,
                            maxWidth: {
                              md: 240,
                              lg: 300,
                              xl: 360,
                            },
                          }}
                        >
                          {item.description || '-'}
                        </Typography>
                      </Tooltip>
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        noWrap
                        display="block"
                        sx={{ fontSize: 9.8, lineHeight: 1.15, mt: 0.15 }}
                      >
                        ID: {item.short_code || item.id}
                      </Typography>
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" noWrap>
                      {item.destination_type || '-'}
                    </Typography>
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      noWrap
                      display="block"
                      sx={{ fontSize: 9.8, lineHeight: 1.15, mt: 0.15 }}
                    >
                      {item.destination || '-'}
                    </Typography>
                  </TableCell>
                  <TableCell align="right">
                    <Typography variant="body2" noWrap>
                      {formatMoney(item.amount)}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Box display="flex" alignItems="center" gap={0.25}>
                      <Typography variant="body2" noWrap>
                        {item.external_reference || '-'}
                      </Typography>
                      {item.external_reference && (
                        <Tooltip title="Copiar referência">
                          <IconButton
                            size="small"
                            onClick={() => onCopy(item.external_reference)}
                          >
                            <ContentCopyIcon sx={{ fontSize: 14 }} />
                          </IconButton>
                        </Tooltip>
                      )}
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" noWrap>
                      {formatDate(item.due_date)}
                    </Typography>
                    {item.status === 'expired' && (
                      <Typography
                        color="error"
                        display="block"
                        fontWeight={700}
                        sx={{ fontSize: 10, lineHeight: 1.1 }}
                      >
                        Expirado
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" noWrap>
                      {formatDateTimeCompact(item.created_at)}
                    </Typography>
                  </TableCell>
                  <TableCell align="right">{renderRowActions(item)}</TableCell>
                </TableRow>
              ))}

              {!loading && !links.length && (
                <TableRow>
                  <TableCell colSpan={9}>{emptyState}</TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {!loading && (links.length > 0 || hasMoreLinks) && (
        <Box
          px={1.15}
          py={0.5}
          display="flex"
          alignItems="center"
          justifyContent="space-between"
          flexWrap="wrap"
          gap={0.75}
          borderTop="1px solid"
          borderColor="divider"
        >
          {links.length > 0 ? (
            <Typography variant="caption" color="text.secondary">
              {`Mostrando ${page * rowsPerPage + 1} a ${Math.min(
                links.length,
                page * rowsPerPage + rowsPerPage
              )} de ${links.length} links carregados`}
            </Typography>
          ) : (
            <Typography variant="caption" color="text.secondary">
              Nenhum resultado entre os links carregados.
            </Typography>
          )}
          <Box display="flex" alignItems="center" gap={0.5}>
            {hasMoreLinks && (
              <Button
                size="small"
                startIcon={<ExpandMoreIcon />}
                disabled={loadingMore}
                onClick={onLoadMore}
                sx={{ mr: 1, whiteSpace: 'nowrap' }}
              >
                {loadingMore
                  ? 'Carregando...'
                  : `Carregar mais (${totalAvailableLinks - loadedLinkCount})`}
              </Button>
            )}
            <Typography variant="caption" color="text.secondary">
              Linhas por página
            </Typography>
            <Select
              size="small"
              variant="standard"
              value={rowsPerPage}
              onChange={(event) => setRowsPerPage(Number(event.target.value))}
              sx={{ mx: 0.5 }}
            >
              <MenuItem value={5}>5</MenuItem>
              <MenuItem value={10}>10</MenuItem>
              <MenuItem value={25}>25</MenuItem>
              <MenuItem value={50}>50</MenuItem>
            </Select>
            <Tooltip title="Página anterior">
              <span>
                <IconButton
                  size="small"
                  disabled={page === 0}
                  onClick={() => setPage((current) => Math.max(0, current - 1))}
                >
                  <ChevronLeftIcon fontSize="small" />
                </IconButton>
              </span>
            </Tooltip>
            <Typography variant="caption">
              {page + 1}/{pageCount}
            </Typography>
            <Tooltip title="Próxima página">
              <span>
                <IconButton
                  size="small"
                  disabled={page >= pageCount - 1}
                  onClick={() =>
                    setPage((current) => Math.min(pageCount - 1, current + 1))
                  }
                >
                  <ChevronRightIcon fontSize="small" />
                </IconButton>
              </span>
            </Tooltip>
          </Box>
        </Box>
      )}
    </Paper>
  );
};

export default LinksList;
